import { addLots } from '@auctisync/api-client';
import { Button } from '@auctisync/ui';
import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * CreateEmptyLotsModal
 *
 * Bulk-creates empty lots on an auction so the field team's Cataloging
 * app has something to work through. Everything but the number — title,
 * description, pricing, specs — is left for the office to fill in after the
 * photos come back. Every lot is a draft bidders cannot see until it is
 * published.
 *
 * Two modes:
 *   - "Add N": the next N lots after the highest number in the sale.
 *   - "Range": lots firstLot..lastLot, skipping any that already exist, so
 *     it is safe to re-run.
 *
 * 🔴 THE DATABASE NUMBERS THEM (`addLots` → `public.add_lots`, 2026-10-05).
 * This used to read the highest number here, in the browser, and insert. A
 * worker can now add a lot from the warehouse at the same moment, so the
 * number is decided once, under a per-sale lock, by the same rule the app
 * uses — control number included. No inventory item is made any more: a lot
 * without one is the norm (`reference_lots_without_inventory_is_the_norm`).
 */
export function CreateEmptyLotsModal({
  auctionId,
  photoFilePrefix,
  existingLotNumbers,
  onDone,
  onClose,
}: {
  auctionId: string;
  photoFilePrefix: string | null;
  existingLotNumbers: number[];
  onDone: (createdCount: number) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'count' | 'range'>('count');
  const [count, setCount] = useState('10');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxExisting = useMemo(
    () => (existingLotNumbers.length > 0 ? Math.max(...existingLotNumbers) : 0),
    [existingLotNumbers],
  );
  const existingSet = useMemo(() => new Set(existingLotNumbers), [existingLotNumbers]);

  function lotNumbersToCreate(): { numbers: number[]; warning: string | null } {
    if (mode === 'count') {
      const n = parseInt(count, 10);
      if (!Number.isFinite(n) || n <= 0) return { numbers: [], warning: null };
      if (n > 500) return { numbers: [], warning: 'Cap is 500 lots per batch.' };
      const start = maxExisting + 1;
      const nums: number[] = [];
      for (let i = 0; i < n; i++) nums.push(start + i);
      return { numbers: nums, warning: null };
    } else {
      const start = parseInt(rangeStart, 10);
      const end = parseInt(rangeEnd, 10);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start < 1 || end < start) {
        return { numbers: [], warning: null };
      }
      if (end - start + 1 > 500) return { numbers: [], warning: 'Cap is 500 lots per batch.' };
      const nums: number[] = [];
      for (let n = start; n <= end; n++) {
        if (!existingSet.has(n)) nums.push(n);
      }
      return { numbers: nums, warning: null };
    }
  }

  const { numbers, warning } = lotNumbersToCreate();
  const skipped =
    mode === 'range' && rangeStart && rangeEnd
      ? Math.max(0, parseInt(rangeEnd, 10) - parseInt(rangeStart, 10) + 1 - numbers.length)
      : 0;

  async function handleCreate() {
    if (numbers.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const added = await addLots(supabase, {
        auctionId,
        lotIds: numbers.map(() => crypto.randomUUID()),
        // "Add N" lets the database number them after the highest; a range
        // names its numbers, and one taken meanwhile is refused.
        ...(mode === 'range' ? { lotNumbers: numbers } : {}),
      });
      if (added.length !== numbers.length) {
        throw new Error('The database added a different number of lots than asked.');
      }

      setBusy(false);
      onDone(numbers.length);
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={backdrop} role="dialog" aria-modal="true" aria-label="Create empty lots">
      <div style={modal}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
            gap: '1rem',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>Create empty lots</h2>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Skeleton lots for the field team to photograph. Office team fills in the details
              later.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.4rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </header>

        {!photoFilePrefix && (
          <div style={warn}>
            No <strong>photo file prefix</strong> is set on this auction. Lots will still be
            created, but control numbers will be blank. Set a prefix in the auction form for
            consistent filename + control-number output.
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <TabButton active={mode === 'count'} onClick={() => setMode('count')}>
            Add N
          </TabButton>
          <TabButton active={mode === 'range'} onClick={() => setMode('range')}>
            Create range
          </TabButton>
        </div>

        {mode === 'count' ? (
          <div style={{ display: 'grid', gap: '0.4rem' }}>
            <label style={labelStyle}>How many lots to add (starting at #{maxExisting + 1})</label>
            <input
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              style={inputStyle}
            />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div>
              <label style={labelStyle}>First lot #</label>
              <input
                type="number"
                min={1}
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                style={inputStyle}
                placeholder="e.g. 50"
              />
            </div>
            <div>
              <label style={labelStyle}>Last lot #</label>
              <input
                type="number"
                min={1}
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                style={inputStyle}
                placeholder="e.g. 99"
              />
            </div>
          </div>
        )}

        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {numbers.length === 0 ? (
            <span style={{ fontStyle: 'italic' }}>Enter a count to preview.</span>
          ) : (
            <>
              Will create <strong style={{ color: 'var(--text)' }}>{numbers.length}</strong> lots: #
              {numbers[0]}
              {numbers.length > 1 ? ` – #${numbers[numbers.length - 1]}` : ''}
              {skipped > 0 && (
                <>
                  {' '}
                  ·{' '}
                  <span style={{ color: 'var(--text-muted)' }}>
                    {skipped} already exist, will skip
                  </span>
                </>
              )}
              {photoFilePrefix && (
                <>
                  {' '}
                  · sample control #{' '}
                  <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                    {photoFilePrefix}-{String(numbers[0]).padStart(4, '0')}
                  </code>
                </>
              )}
            </>
          )}
        </div>

        {warning && <div style={warn}>{warning}</div>}
        {error && <div style={errStyle}>{error}</div>}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '1rem',
          }}
        >
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || numbers.length === 0}
            onClick={() => {
              void handleCreate();
            }}
          >
            {busy ? 'Creating…' : `Create ${numbers.length} lot${numbers.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '0.45rem 0.85rem',
        borderRadius: '0.3rem',
        border: '1px solid var(--border)',
        background: active ? 'var(--brand-primary)' : 'var(--surface)',
        color: active ? 'var(--brand-primary-fg)' : 'var(--text)',
        fontSize: '0.85rem',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '4rem 1rem',
  zIndex: 100,
};

const modal: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  maxWidth: '32rem',
  width: '100%',
  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 500,
  color: 'var(--text-muted)',
  marginBottom: '0.2rem',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '0.3rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};

const warn: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: '0.3rem',
  background: 'var(--surface-warning, #fef9c3)',
  color: 'var(--text-warning, #854d0e)',
  border: '1px solid #fde047',
  fontSize: '0.8rem',
  marginTop: '0.5rem',
};

const errStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: '0.3rem',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
  marginTop: '0.5rem',
};
